import { Bars3Icon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import {
  contactInfoScreenOptions,
  defaultPullThroughRatioScreens,
} from 'components/PullThroughRatioPartiesTable/pullThroughRatioScreens'
import { Sortable } from 'components/Sortable'
import { AccountType, LoanFieldProps, loanStatusConstants } from 'config'
import { setChannelTypeOptions } from 'config/channel.constants'
import { accountTypeWithBorrower } from 'config/user.type.constants'
import { orderServicingMenuKeys, servicingMenuLabels } from 'pages/Loan/Servicing/menuItems'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  accountTypeTitleForName,
  applyChannelOptions,
  defaultChannelOptions,
  getLoanTypeMapForSelect,
  originalLoanTypeMap,
  partiesMap,
  partiesMapWithAccountTypes,
  partyViewSelectionForOptions,
  pullThroughRatioPartiesMap,
} from 'reducers/loanDetail.reducer'
import { getAdminConfig, getRolodexCategories, setAdminConfig } from 'services'
import { ButtonGroup, Checkbox, Tooltip } from 'stories/components'
import {
  capitalizeFirstLetter,
  confirm,
  getOverviewCopyPlaceholders,
  InputConvert,
  InputValidate,
  isEmpty,
  toCamelCase,
} from 'utils'
import { getLoanFieldSubMenus } from 'utils/loanFieldSubMenus.util'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputsV2 } from './constants'

const tabs: Record<string, Array<string>> = {
  views: [
    'pipelineSection',
    // 'nonServicingLoanStatus',
    'servicingLoanStatus',
    'borrowerLoanStatus',
    'pipelineTableSection',
    'overviewSection',
    'overviewCopyTemplate',
    'overviewFieldsOrder',
    'loanNumber',
    'entityName',
    'borrowerName',
    'propertyAddress',
    'loanProgram',
    'mers',
    'closingDate',
    'thirdPartyId',
    'coBorrowerGuarantor',
    'productSection',
    'productType',
    'productProgram',
    'productRate',
    'productPrice',
    'productRateDate',
    'productLockDays',
    'productRateSpread',
    'productExitFee',
    'partySection',
    'partyView',
    'partyInfo',
    'pullThroughRatioSection',
    'pullThroughRatioView',
    'hidePullThroughRatio',
    'pullThroughRatioKeys',
    'pullThroughRatioScreens',
    'channelOptionsSection',
    'channelOptions',
    'pipelineSection',
    'defaultPipeline',
    'notificationsSection',
    'notificationsEnabled',
    'servicingMenuSection',
    'servicingMenuOrder',
  ],
  security: [
    'mfaEnabled',
    'jwtTokenExpiresIn',
    'warningPwChangeInValue',
    'requirePwChangeInValue',
    'documentSection',
    'excelPassword',
  ],
  setup: ['lei', 'mersId', 'eSignSection', 'eSignBrand', 'regionalSection', 'defaultTimezone'],
}

export const GlobalSettingsV2 = ({ tab }: { tab?: string }) => {
  const dispatch = useDispatch()
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  // The tenant's own Custom Information pages are menu entries too, so they order and hide with
  // the built-in ones.
  const loanFields = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
  )
  const globalSettingV2 = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.GlobalSettingV2])
  const [account, setAccount] = useState(AccountType.ADMIN)
  const [data, setData] = useState<Record<string, any>>({})
  const [inputs, setInputs] = useState(defaultInputsV2())
  const [action, setAction] = useState('')
  const pullThroughRatioOrderRef = useRef<string[] | null>(null)
  const servicingMenuOrderRef = useRef<string[] | null>(null)
  const [contactInfoPartyOptions, setContactInfoPartyOptions] = useState<Record<string, string>>({})

  let defaultNonServicingStatus: Record<string, boolean> = {},
    defaultServicingStatus: Record<string, boolean> = {}
  Object.keys(loanGlobalStatus).map((v: string) => {
    defaultNonServicingStatus[v] = false
  })
  Object.keys(loanStatusConstants.servicingStatus).map((v: string) => {
    defaultServicingStatus[v] = false
  })

  const initData = async () => {
    const [data, categories] = await Promise.all([getAdminConfig('globalSettingsV2'), getRolodexCategories()])

    const tempOptions = partiesMapWithAccountTypes(true)
    const contactInfoOptions: Record<string, string> = {
      ...partiesMapWithAccountTypes(),
      borrower: 'Borrower',
      lender: 'Lender',
    }
    Object.keys(categories)
      .sort((a, b) => (categories[a] > categories[b] ? 1 : -1))
      .map((key: any) => {
        const categoryKey = capitalizeFirstLetter(toCamelCase(key))
        tempOptions[categoryKey] = accountTypeTitleForName(categories[key])
        contactInfoOptions[categoryKey] = accountTypeTitleForName(categories[key])
      })

    setContactInfoPartyOptions(contactInfoOptions)
    setData(data)
    const temp = cloneDeep(inputs)
    Object.keys(temp).forEach((key) => {
      temp[key].value = data[key] !== undefined && data[key] !== null ? data[key] : temp[key].value
      if (key === 'partyView') {
        ;(temp[key] as any).options = tempOptions
        temp[key].value = partyViewSelectionForOptions(data[key][account])
      }
      if (key === 'pullThroughRatioView') {
        const hidePtr = !!data.hidePullThroughRatio
        temp[key].title = hidePtr ? 'Contact Info View' : 'Pull Through Ratio Party View'
        ;(temp[key] as any).options = hidePtr
          ? contactInfoOptions
          : Object.fromEntries(
              Object.entries(pullThroughRatioPartiesMap)
                .filter(([k]) => k !== 'lender')
                .map(([k, label]) => [k, accountTypeTitleForName(label)]),
            )
      }
      if (key === 'channelOptions') {
        temp[key].value = { ...defaultChannelOptions(), ...(data.channelOptions || {}) }
        setChannelTypeOptions(applyChannelOptions(temp[key].value))
      }
      if (key === 'defaultChannel') {
        ;(temp[key] as any).options = getLoanTypeMapForSelect(temp[key].value)
      }
      if (key === 'pullThroughRatioScreens') {
        temp[key].value = { ...defaultPullThroughRatioScreens(), ...(data.pullThroughRatioScreens || {}) }
      }
    })
    setInputs(temp)
  }

  useEffect(() => {
    initData()
  }, [])

  const onChange = async (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)

    if (key === 'pullThroughRatioView' && Array.isArray(value)) {
      const prevOrder: string[] = Array.isArray(newInputs[key].value) ? newInputs[key].value : []
      const kept = prevOrder.filter((k) => value.includes(k))
      const added = value.filter((k: string) => !kept.includes(k))
      value = [...kept, ...added]
    }

    if (key === 'mfaEnabled') {
      const wasEnabled = !!newInputs[key].value
      const isTurningOn = !wasEnabled && value
      const isTurningOff = wasEnabled && !value

      let applyMfaToAllUsers = false
      if (isTurningOn || isTurningOff) {
        const message = isTurningOn
          ? 'Do you want to change everyone to 2FA authentication?'
          : 'Do you want to remove 2FA authentication for everyone?'
        applyMfaToAllUsers = !!(await confirm(message))
      }

      newInputs[key].error = InputValidate({ ...newInputs[key], value })
      newInputs[key].value = value
      setInputs(newInputs)

      if (isTurningOn || isTurningOff) {
        await onSubmit(newInputs, applyMfaToAllUsers)
      }

      return newInputs
    }

    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    if (key === 'partyView') {
      const temp = cloneDeep(data)
      temp[key][account] = value
      setData(temp)
    }

    return newInputs
  }

  const onSubmit = async (_newInputs?: Record<string, any>, applyMfaToAllUsers?: boolean) => {
    let hasError = false
    let newInputs = _newInputs || cloneDeep(inputs)

    const rtl: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      if (key === 'partyView') {
        rtl[key] = data[key]
        Object.keys(rtl[key] || {}).forEach((accountKey) => {
          rtl[key][accountKey] = partyViewSelectionForOptions(rtl[key][accountKey])
        })
        rtl[key][account] = partyViewSelectionForOptions(newInputs[key].value)
      } else rtl[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    rtl.pipeline = data.pipeline
    if (applyMfaToAllUsers !== undefined) rtl.applyMfaToAllUsers = applyMfaToAllUsers
    setInputs(newInputs)
    if (hasError) return
    setAction('setting')
    const saved = { ...rtl }
    delete saved.applyMfaToAllUsers
    await setAdminConfig('globalSettingsV2', rtl)
    dispatch(setGlobalConfig(GlobalConfigType.GlobalSettingV2, { ...globalSettingV2, ...saved }))
    if (rtl.channelOptions) setChannelTypeOptions(applyChannelOptions(rtl.channelOptions))
    setAction('')
  }

  const renderPullThroughRatioScreens = () => {
    let screens = inputs.pullThroughRatioScreens?.value
    if (!screens || typeof screens !== 'object') screens = defaultPullThroughRatioScreens()
    screens = { ...defaultPullThroughRatioScreens(), ...screens }
    const screenOptions = contactInfoScreenOptions()

    const onToggle = async (key: string) => {
      const next = cloneDeep(screens)
      next[key] = !next[key]
      const newInputs = await onChange('pullThroughRatioScreens', next)
      await onSubmit(newInputs)
    }

    return (
      <div className="md:col-span-2">
        <div className="text-sm text-gray-600 mb-2">
          Choose which screens show the {inputs.hidePullThroughRatio?.value ? 'Contact Info' : 'Pull Through Ratio'}{' '}
          parties table.
        </div>
        <div className="grid grid-cols-3 gap-x-2">
          {Object.keys(screenOptions).map((key, index) => (
            <div className="col-span-1" key={key}>
              <div className="flex gap-2 items-center p-2">
                <span className={`flex-1 ${!screens[key] ? 'line-through opacity-50' : ''}`}>
                  {index + 1}. {screenOptions[key].title}
                </span>
                <Tooltip message="Show / Hide">
                  <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => onToggle(key)}>
                    {screens[key] ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="text-gray-300 w-4 h-4" />
                    )}
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderChannelOptions = () => {
    let channelOptions = inputs.channelOptions?.value
    if (!channelOptions || typeof channelOptions !== 'object') channelOptions = defaultChannelOptions()
    channelOptions = { ...defaultChannelOptions(), ...channelOptions }

    const onChangeValue = async (key: string, map: 'title' | 'visible', value: string | boolean) => {
      const next = cloneDeep(channelOptions)
      if (!next[key]) next[key] = { title: originalLoanTypeMap[key], visible: true }
      next[key][map] = value
      const newInputs = await onChange('channelOptions', next)
      await onSubmit(newInputs)
    }

    return (
      <div className="md:col-span-2">
        <table className="text-sm w-full max-w-2xl">
          <thead className="uppercase text-xs bg-gray-50">
            <tr>
              {['No', 'Original', 'New', 'In Use'].map((v) => (
                <th scope="col" className="p-3 border" key={`channel-header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(originalLoanTypeMap).map((key, index) => {
              const { title, visible } = channelOptions[key] || {
                title: originalLoanTypeMap[key],
                visible: true,
              }
              return (
                <tr key={key}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="p-2 border">{originalLoanTypeMap[key]}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChangeValue(key, 'title', value)}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={`channel-visible-${key}`}
                      title=""
                      value={visible !== false}
                      onChange={(value) => onChangeValue(key, 'visible', value)}
                      className="w-[none]"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const renderPullThroughRatioOrder = () => {
    const hidePtr = !!inputs.hidePullThroughRatio?.value
    const options: Record<string, string> = hidePtr
      ? Object.keys(contactInfoPartyOptions).length
        ? contactInfoPartyOptions
        : pullThroughRatioPartiesMap
      : Object.fromEntries(Object.entries(pullThroughRatioPartiesMap).filter(([k]) => k !== 'lender'))

    const selectedOrder: string[] = (
      Array.isArray(inputs.pullThroughRatioView.value) ? inputs.pullThroughRatioView.value : []
    ).filter((key: string) => options[key])

    const sortableData = selectedOrder.map((key) => ({
      id: key,
      data: { key, label: options[key] || key },
    }))

    const onChangeOrder = (newData: any[]) => {
      const newOrder = newData.map((item) => item.id)
      pullThroughRatioOrderRef.current = newOrder
      const newInputs = cloneDeep(inputs)
      newInputs.pullThroughRatioView.value = newOrder
      setInputs(newInputs)
    }

    const onDropOrder = async () => {
      const finalOrder = pullThroughRatioOrderRef.current
      pullThroughRatioOrderRef.current = null
      if (!finalOrder) return
      const newInputs = cloneDeep(inputs)
      newInputs.pullThroughRatioView.value = finalOrder
      setInputs(newInputs)
      await onSubmit(newInputs)
    }

    const renderItem = (itemData: any) => (
      <div className="inline-flex items-center gap-1.5 bg-white border border-slate-300 rounded-full pl-2 pr-3 py-1 text-sm whitespace-nowrap hover:border-shade-blue cursor-move select-none">
        <Tooltip message="Drag to reorder">
          <Bars3Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
        </Tooltip>
        <span>{itemData.label}</span>
      </div>
    )

    return (
      <div>
        <div className="text-xs text-gray-500 mb-2">
          Drag to set the display order of the selected {hidePtr ? 'contacts' : 'parties'} above.
        </div>
        {sortableData.length ? (
          <div className="flex flex-wrap gap-1.5">
            <Sortable data={sortableData} setData={onChangeOrder} onDrop={onDropOrder} renderItem={renderItem} />
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">None selected</div>
        )}
      </div>
    )
  }

  const renderPullThroughRatioKeys = () => {
    const pullThroughRatioView = (inputs.pullThroughRatioView.value || []).filter(
      (key: string) => !(key === 'lender' && !inputs.hidePullThroughRatio?.value),
    )
    let pullThroughRatioKeys = inputs.pullThroughRatioKeys.value
    if (!pullThroughRatioKeys || typeof pullThroughRatioKeys != 'object') pullThroughRatioKeys = {}

    const onChangeValue = async (key: string, value: string) => {
      pullThroughRatioKeys[key] = value
      const newInputs = await onChange('pullThroughRatioKeys', pullThroughRatioKeys)
      await onSubmit(newInputs)
    }

    return (
      <div className="grid grid-cols-3 gap-4">
        {pullThroughRatioView.map((key: any) => (
          <div>
            <RenderInput
              input={{
                inputType: 'select',
                options: {
                  companyName: 'Company Name',
                  name: 'Name',
                },
                value: pullThroughRatioKeys[key],
                defaultSelected: false,
                title: contactInfoPartyOptions[key] || partiesMap[key] || key,
              }}
              Key={key}
              onChange={onChangeValue}
            />
          </div>
        ))}
      </div>
    )
  }

  /**
   * Admin > Configuration > Global Settings > Servicing > Servicing Menu. Drag to set the order of
   * the per-loan Servicing menu, and hide the entries this tenant does not use.
   *
   * The list is every entry the menu could show, not the ones this admin can see: the setting is
   * tenant-wide, and an entry hidden from the admin's own role would otherwise drop out of the
   * order they save. Which of them a given servicer actually gets is still decided by their
   * permissions on top of this order.
   *
   * Hiding is presentation only. The entry leaves the menu, but the screen stays reachable by URL -
   * Page Permissions is what actually withholds one.
   */
  const renderServicingMenu = () => {
    const labels = servicingMenuLabels(getLoanFieldSubMenus(loanFields))
    const savedOrder: string[] = Array.isArray(inputs.servicingMenuOrder?.value) ? inputs.servicingMenuOrder.value : []
    const order = orderServicingMenuKeys(Object.keys(labels), savedOrder)

    let visible = inputs.servicingMenuVisible?.value
    if (!visible || typeof visible !== 'object') visible = {}

    const sortableData = order.map((key) => ({ id: key, data: { key, label: labels[key] || key } }))

    const saveOrder = async (newOrder: string[]) => {
      const newInputs = await onChange('servicingMenuOrder', newOrder)
      await onSubmit(newInputs)
    }

    // Save once the drag ends, not on every row the pointer crosses on the way.
    const onChangeOrder = (newData: any[]) => {
      const newOrder = newData.map((item) => item.id)
      servicingMenuOrderRef.current = newOrder
      const newInputs = cloneDeep(inputs)
      newInputs.servicingMenuOrder.value = newOrder
      setInputs(newInputs)
    }

    const onDropOrder = async () => {
      const finalOrder = servicingMenuOrderRef.current
      servicingMenuOrderRef.current = null
      if (finalOrder) await saveOrder(finalOrder)
    }

    const onToggleVisible = async (key: string) => {
      const next = { ...visible, [key]: visible[key] === false }
      const newInputs = await onChange('servicingMenuVisible', next)
      await onSubmit(newInputs)
    }

    const renderItem = (itemData: any, index: number) => {
      const isVisible = visible[itemData.key] !== false
      return (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-300 rounded px-3 py-2 mb-2 hover:bg-gray-100">
          <Tooltip message="Drag to reorder">
            <Bars3Icon className="w-4 h-4 text-gray-500 flex-shrink-0 cursor-move" />
          </Tooltip>
          <span className={`flex-1 ${isVisible ? '' : 'line-through opacity-50'}`}>
            {index + 1}. {itemData.label}
          </span>
          <Tooltip message={isVisible ? 'Hide from the menu' : 'Show in the menu'}>
            <span
              className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
              onClick={() => onToggleVisible(itemData.key)}
            >
              {isVisible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="text-gray-300 w-4 h-4" />}
            </span>
          </Tooltip>
        </div>
      )
    }

    return (
      <div className="md:col-span-2 max-w-xl">
        <div className="text-sm text-gray-600 mb-1">
          Drag to set the order of the menu on a loan&apos;s Servicing screen, and hide the entries you do not use.
        </div>
        <div className="text-xs text-gray-500 mb-2">
          Hiding an entry takes it out of the menu only - use Page Permissions to withhold a screen. A screen added in a
          later release joins the end of the list.
        </div>
        <div className="mb-3 text-sm">
          <span className="text-shade-blue hover:underline cursor-pointer" onClick={() => saveOrder([])}>
            Reset to default order
          </span>
        </div>
        <Sortable data={sortableData} setData={onChangeOrder} onDrop={onDropOrder} renderItem={renderItem} />
      </div>
    )
  }

  const renderOverviewCopyTemplate = () => {
    const placeholders = getOverviewCopyPlaceholders()
    const value =
      inputs.overviewCopyTemplate?.value || '{LoanNumber} | {EntityName} | {BorrowerName} | {SubjectPropertyAddress}'

    const insertPlaceholder = async (key: string) => {
      const next = `${value}${value && !String(value).endsWith(' ') ? ' ' : ''}{${key}}`
      const newInputs = await onChange('overviewCopyTemplate', next)
      await onSubmit(newInputs)
    }

    return (
      <div className="md:col-span-2">
        <div className="text-sm text-gray-600 mb-2">
          Used by the Loan Overview copy icon and the default Emails/Notes subject. Click a placeholder to append it.
          Default: <code>{'{LoanNumber} | {EntityName} | {BorrowerName} | {SubjectPropertyAddress}'}</code>
        </div>
        <RenderInput
          input={{
            inputType: 'textarea',
            title: 'Overview Copy / Notes Subject Template',
            value,
            rows: 3,
          }}
          Key="overviewCopyTemplate"
          onChange={(_: string, v: string) => onChange('overviewCopyTemplate', v)}
          onBlur={() => onSubmit()}
        />
        <div className="border-b mt-3 mb-2 text-sm font-medium">Placeholders</div>
        <pre className="flex gap-1 flex-wrap text-sm whitespace-pre-wrap">
          {placeholders.map((key) => (
            <div
              key={key}
              className="mr-2 cursor-pointer hover:underline text-shade-blue"
              onClick={() => insertPlaceholder(key)}
            >
              {key},{' '}
            </div>
          ))}
        </pre>
      </div>
    )
  }

  const renderOverviewFieldsOrder = () => {
    const fieldLabels: Record<string, string> = {
      loanEmail: 'Loan Email',
      loanNumber: 'Loan Number',
      entityName: 'Entity Name',
      borrowerName: 'Borrower Name',
      preferredLanguage: 'Preferred Language',
      propertyAddress: 'Property Address',
      loanProgram: 'Loan Program',
      // Read-only: the holders come from the loan's participations, so the Editable toggle has no
      // effect on this one. It is listed so it can still be reordered or hidden.
      lenderAccount: 'Lender Account',
      mers: 'MERS',
      closingDate: 'Closing Date',
      // Read-only for the same reason as Lender Account: each is a servicing figure read from the
      // service that owns it, so the Editable toggle does nothing. Listed so they can still be
      // reordered or hidden. They draw only on a loan that is being serviced.
      maturityDate: 'Maturity Date',
      nextPaymentDate: 'Next Payment Due',
      daysLate: 'Days Late',
      thirdPartyId: 'ThirdParty ID',
      coBorrowerGuarantor: 'Co-Borrower / Guarantor Contacts',
    }

    const defaultOrder = [
      'loanEmail',
      'loanNumber',
      'entityName',
      'borrowerName',
      'preferredLanguage',
      'propertyAddress',
      'loanProgram',
      'lenderAccount',
      'mers',
      'closingDate',
      'maturityDate',
      'nextPaymentDate',
      'daysLate',
      'thirdPartyId',
      'coBorrowerGuarantor',
    ]
    // DB/json sometimes nests a key as ["thirdPartyId"] instead of "thirdPartyId"; flatten so
    // includes() and the sortable list stay string-keyed.
    const savedOrder = (Array.isArray(inputs.overviewFieldsOrder.value) ? inputs.overviewFieldsOrder.value : [])
      .flat(Infinity)
      .filter((key: unknown): key is string => typeof key === 'string' && key.length > 0)
      .filter((key: string, index: number, arr: string[]) => arr.indexOf(key) === index)
    const hasSavedOrder = savedOrder.length > 0
    // Fields the saved order predates are appended, or a newly added one can never be reordered
    // or hidden here because it is not in the list being rendered. Loan Email is the exception:
    // it belongs at the top until an admin drags it.
    const mergedOrder = hasSavedOrder
      ? [...savedOrder, ...defaultOrder.filter((key) => key !== 'loanEmail' && !savedOrder.includes(key))]
      : defaultOrder
    const fieldsOrder = mergedOrder.includes('loanEmail') ? mergedOrder : ['loanEmail', ...mergedOrder]

    // Initialize enabled state for each field
    let fieldsEnabled = inputs.overviewFieldsEnabled?.value
    if (!fieldsEnabled || typeof fieldsEnabled !== 'object') {
      fieldsEnabled = {}
      defaultOrder.forEach((key) => {
        fieldsEnabled[key] = key !== 'loanEmail'
      })
    }

    // Initialize editable state for each field
    let fieldsEditable = inputs.overviewFieldsEditable?.value
    if (!fieldsEditable || typeof fieldsEditable !== 'object') {
      fieldsEditable = {}
      defaultOrder.forEach((key) => {
        fieldsEditable[key] = false // Default to not editable
      })
    }

    const sortableData = fieldsOrder.map((key: string) => ({
      id: key,
      data: { key, label: fieldLabels[key] || key },
    }))

    const onChangeOrder = async (newData: any[]) => {
      const newOrder = newData.map((item) => item.id)
      const newInputs = await onChange('overviewFieldsOrder', newOrder)
      await onSubmit(newInputs)
    }

    const onChangeEnabled = async (fieldKey: string, enabled: boolean) => {
      const updatedEnabled = { ...fieldsEnabled, [fieldKey]: enabled }
      const newInputs = await onChange('overviewFieldsEnabled', updatedEnabled)
      await onSubmit(newInputs)
    }

    const onChangeEditable = async (fieldKey: string, editable: boolean) => {
      const updatedEditable = { ...fieldsEditable, [fieldKey]: editable }
      const newInputs = await onChange('overviewFieldsEditable', updatedEditable)
      await onSubmit(newInputs)
    }

    // Fields whose value is derived rather than stored on the loan: a generated address, the
    // holders read off the loan's participations, and the servicing figures read from the services
    // that own them. An override has nowhere to go on any of them, so they get no Editable control
    // instead of one that silently does nothing.
    const readOnlyFields = ['loanEmail', 'lenderAccount', 'maturityDate', 'nextPaymentDate', 'daysLate']

    const renderItem = (itemData: any) => {
      const fieldKey = itemData.key
      // Loan Email stays off until turned on. Every other field is visible unless explicitly hidden.
      const isEnabled = fieldKey === 'loanEmail' ? fieldsEnabled[fieldKey] === true : fieldsEnabled[fieldKey] !== false
      const isEditable = fieldsEditable[fieldKey] === true // Default to false if not set

      return (
        <div className="bg-gray-50 border border-gray-300 rounded px-4 py-2 mb-2 hover:bg-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <span className="mr-2">☰</span>
              <span className="font-medium">{itemData.label}</span>
            </div>
            <div className="flex items-center gap-4 ml-4">
              {!readOnlyFields.includes(fieldKey) && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Editable</span>
                <RenderInput
                  input={{
                    inputType: 'toggle',
                    value: isEditable,
                    title: '',
                  }}
                  Key={`overviewFieldsEditable_${fieldKey}`}
                  onChange={(_: string, value: boolean) => onChangeEditable(fieldKey, value)}
                />
              </div>
              )}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Visible</span>
                <RenderInput
                  input={{
                    inputType: 'toggle',
                    value: isEnabled,
                    title: '',
                  }}
                  Key={`overviewFieldsEnabled_${fieldKey}`}
                  onChange={(_: string, value: boolean) => onChangeEnabled(fieldKey, value)}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="max-w-md">
        <div className="text-sm text-gray-600 mb-2">Drag to reorder the fields:</div>
        <Sortable data={sortableData} setData={onChangeOrder} renderItem={renderItem} />
      </div>
    )
  }

  return (
    <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <LayoutLoading show={!isEmpty(action)} />
      {Object.keys(data).length
        ? Object.keys(inputs).map((key, index) => {
            if (tab && !tabs[tab].includes(key)) return
            let input = inputs[key]
            if (['nonServicingLoanStatus', 'borrowerLoanStatus', 'activeLoanStatusForAEDisable'].includes(key))
              input.value = { ...defaultNonServicingStatus, ...input.value }
            if (key == 'servicingLoanStatus') input.value = { ...defaultServicingStatus, ...input.value }
            if (key === 'partyView') input.value = partyViewSelectionForOptions(data.partyView[account])
            if (key === 'pullThroughRatioView') {
              const hidePtr = !!inputs.hidePullThroughRatio?.value
              input.title = hidePtr ? 'Contact Info View' : 'Pull Through Ratio Party View'
              ;(input as any).options = hidePtr
                ? Object.keys(contactInfoPartyOptions).length
                  ? contactInfoPartyOptions
                  : (input as any).options
                : Object.fromEntries(
                    Object.entries(pullThroughRatioPartiesMap)
                      .filter(([k]) => k !== 'lender')
                      .map(([k, label]) => [k, accountTypeTitleForName(label)]),
                  )
            }
            if (key == 'pullThroughRatioKeys') (input as any).render = renderPullThroughRatioKeys
            if (key == 'pullThroughRatioScreens') (input as any).render = renderPullThroughRatioScreens
            if (key == 'channelOptions') (input as any).render = renderChannelOptions
            if (key == 'overviewFieldsOrder') (input as any).render = renderOverviewFieldsOrder
            if (key == 'overviewCopyTemplate') (input as any).render = renderOverviewCopyTemplate
            if (key == 'servicingMenuOrder') (input as any).render = renderServicingMenu

            const rtl = (
              <>
                <div className={`input md:col-span-${input.span || 1} `} key={index}>
                  <RenderInput input={input} Key={key} onChange={onChange} onBlur={() => onSubmit()} />
                </div>
                {key === 'partySection' && (
                  <div className="col-span-full" key={`party-${index}`}>
                    <ButtonGroup title={accountTypeWithBorrower()} value={account} onChange={setAccount} sort />
                  </div>
                )}
                {key === 'pullThroughRatioView' && (
                  <div className="input md:col-span-2" key={`pull-through-ratio-order-${index}`}>
                    {renderPullThroughRatioOrder()}
                  </div>
                )}
              </>
            )
            return rtl
          })
        : null}
    </div>
  )
}
